import { useTranslation } from 'react-i18next';
import {
  Department,
  TICKET_PRIORITY_ORDER,
  type TicketPriority,
  departmentLabelKey,
  ticketPriorityLabelKey,
} from '@nbh/shared';

export interface ReportDraft {
  title: string;
  description: string;
  priority: TicketPriority;
  department: Department;
}

export interface ReportFieldsProps {
  draft: ReportDraft;
  onChange: (patch: Partial<ReportDraft>) => void;
}

const FIELD_CLASS =
  'w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-3 text-base text-slate-100';

/** Form fields for a fault report. Controls are full width and ≥48px for gloved hands. */
export function ReportFields({ draft, onChange }: ReportFieldsProps) {
  const { t } = useTranslation();

  return (
    <>
      <label className="block">
        <span className="mb-1 block text-sm text-slate-300">{t('report.title')}</span>
        <input
          value={draft.title}
          onChange={(event) => onChange({ title: event.target.value })}
          maxLength={160}
          required
          placeholder={t('report.titlePlaceholder')}
          className={FIELD_CLASS}
        />
      </label>

      <label className="block">
        <span className="mb-1 block text-sm text-slate-300">{t('report.priority')}</span>
        <select
          value={draft.priority}
          onChange={(event) => onChange({ priority: event.target.value as TicketPriority })}
          className={FIELD_CLASS}
        >
          {TICKET_PRIORITY_ORDER.map((level) => (
            <option key={level} value={level}>
              {t(ticketPriorityLabelKey[level])}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="mb-1 block text-sm text-slate-300">{t('report.department')}</span>
        <select
          value={draft.department}
          onChange={(event) => onChange({ department: event.target.value as Department })}
          className={FIELD_CLASS}
        >
          {Object.values(Department).map((value) => (
            <option key={value} value={value}>
              {t(departmentLabelKey[value])}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="mb-1 block text-sm text-slate-300">{t('report.description')}</span>
        <textarea
          value={draft.description}
          onChange={(event) => onChange({ description: event.target.value })}
          rows={3}
          maxLength={4000}
          className={FIELD_CLASS}
        />
      </label>
    </>
  );
}
